// Entry point. Decides between booting the app and showing the install gate.
// The app itself is a lazy chunk: a phone visitor in a browser tab downloads only
// this file, the gate markup already in index.html, and the stylesheet.

import { showBootError } from './boot-error';
import { captureInstallPrompt, decideGate, showGate } from './gate';
import { readSettings } from '$lib/db/idb-raw';
import { SETTING_LANGUAGE, SETTING_USER_PHONE } from '$lib/db/setting-keys';

performance.mark('app:entry');

// Must be registered as early as possible — Chromium fires `beforeinstallprompt`
// once, and only listeners that already exist receive it.
captureInstallPrompt();

const decision = decideGate();
if (decision.boot) {
  document.getElementById('gate')?.remove();

  // Started here, deliberately, and NOT awaited. These two rows are what the
  // first screen waits on — which language to speak and whether a number was
  // ever verified — and opening IndexedDB is the single most variable step in
  // the whole launch: tens of milliseconds warm, seconds cold. Firing it now
  // runs it alongside the app chunk's download instead of after it, so the two
  // slow things overlap. Read through idb-raw so Dexie stays out of this file.
  const settings = readSettings([SETTING_LANGUAGE, SETTING_USER_PHONE]);

  // The splash stays up; boot.ts takes it down once the app has real content —
  // or showBootError does, if there is never going to be any. Both the chunk
  // download and boot() itself are covered: offline with an incomplete precache
  // fails at the import, a broken database fails inside boot.
  let started = false;
  void import('./app/boot')
    .then((module) => module.boot(settings))
    .then(() => {
      started = true;
    })
    .catch(showBootError);

  // Backstop for a throw that never reaches that chain. Both are scoped to the
  // pre-mount window: once the app is up, a failed image or a stray rejection
  // must not replace a working screen with an error card.
  addEventListener('error', (event) => {
    // Resource errors (an <img> that 404s) also land here and carry no `error`.
    // They are not a failure to start.
    if (!started && event.error !== undefined && event.error !== null) {
      showBootError(event.error);
    }
  });
  addEventListener('unhandledrejection', (event) => {
    if (!started) {
      showBootError(event.reason);
    }
  });
} else {
  document.getElementById('splash')?.remove();
  showGate(decision);
}
